import * as React from 'react';
import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, DatePicker, Dropdown, Empty, Layout, Menu, Modal, Space, Spin, Tag, Typography, notification } from 'antd';
import {
    BarChartOutlined,
    DollarOutlined,
    DownloadOutlined,
    EditOutlined,
    FileTextOutlined,
    FolderOpenOutlined,
    ReloadOutlined,
    TeamOutlined,
    ThunderboltOutlined,
    UnorderedListOutlined,
} from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import axiosApiInstance from '../../../api/axiosClient';
import { useCampaignQuery, useRefreshCampaign } from './queryHooks';
import { formatDateRange, formatRefreshed } from './format';
import type { Campaign } from './types';

const { Sider, Content } = Layout;
const SIDER_KEY = 'campaign-sider-collapsed';

const SECTIONS: { key: string; label: string; icon: React.ReactNode }[] = [
    { key: 'creators', label: 'Creators', icon: <TeamOutlined /> },
    { key: 'content', label: 'Content', icon: <FolderOpenOutlined /> },
    { key: 'performance', label: 'Performance', icon: <ThunderboltOutlined /> },
    { key: 'summary', label: 'Summary', icon: <BarChartOutlined /> },
    { key: 'reports', label: 'Reports', icon: <FileTextOutlined /> },
    { key: 'payments', label: 'Payments', icon: <DollarOutlined /> },
    { key: 'tasks', label: 'Tasks', icon: <UnorderedListOutlined /> },
];

const TITLES: Record<string, string> = {
    creators: 'Creators',
    content: 'Content',
    performance: 'Performance',
    summary: 'Summary',
    reports: 'Reports',
    payments: 'Payments',
    tasks: 'Tasks',
};

function activeSection(pathname: string): string {
    const parts = pathname.split('/').filter(Boolean);
    const idx = parts.findIndex((p) => p === 'campaigns');
    const section = parts[idx + 2];
    if (!section || /^\d+$/.test(section)) return 'creators';
    return section;
}

const CampaignLayout: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const campaignId = id ? Number(id) : undefined;
    const navigate = useNavigate();
    const location = useLocation();
    const { data: campaign, isLoading, isError } = useCampaignQuery(campaignId);
    const refresh = useRefreshCampaign();
    const qc = useQueryClient();
    const [datesOpen, setDatesOpen] = React.useState(false);
    const [dateStart, setDateStart] = React.useState<Dayjs | null>(null);
    const [dateEnd, setDateEnd] = React.useState<Dayjs | null>(null);
    const [savingDates, setSavingDates] = React.useState(false);
    const section = activeSection(location.pathname);
    const [siderCollapsed, setSiderCollapsed] = React.useState(
        () => window.localStorage.getItem(SIDER_KEY) === '1',
    );
    const onSiderCollapse = (next: boolean) => {
        setSiderCollapsed(next);
        window.localStorage.setItem(SIDER_KEY, next ? '1' : '0');
    };

    React.useEffect(() => {
        if (!campaign?.last_refreshed_at) return;
        qc.invalidateQueries({ queryKey: ['campaigns', 'posts'] });
        qc.invalidateQueries({ queryKey: ['campaigns', 'summary'] });
        qc.invalidateQueries({ queryKey: ['campaigns', 'performance'] });
        qc.invalidateQueries({ queryKey: ['campaigns', 'payments'] });
        qc.invalidateQueries({ queryKey: ['campaigns', 'reports'] });
        qc.invalidateQueries({ queryKey: ['campaigns', 'report'] });
    }, [campaign?.last_refreshed_at, qc]);

    // The header polls the campaign while a refresh is running. Keep the post
    // list on that same cadence, or the cards freeze while "Refreshing…" stays up.
    React.useEffect(() => {
        if (!campaignId || campaign?.refresh_status !== 'running') return;
        const timer = window.setInterval(() => {
            qc.invalidateQueries({ queryKey: ['campaigns', 'posts', campaignId] });
        }, 2000);
        return () => window.clearInterval(timer);
    }, [campaign?.refresh_status, campaignId, qc]);

    const runRefresh = async () => {
        if (!campaignId) return;
        try {
            await refresh.mutateAsync(campaignId);
            notification.success({ message: 'Refresh started', placement: 'bottomRight', duration: 2 });
        } catch (e: any) {
            const msg = e?.response?.data?.error || 'Refresh failed';
            notification.error({ message: msg });
        }
    };

    const [exporting, setExporting] = React.useState(false);
    const onExport = async () => {
        if (!campaign?.id) return;
        setExporting(true);
        try {
            const res = await axiosApiInstance.get(`/api/campaigns/items/${campaign.id}/export`, { responseType: 'blob' });
            const disposition = String(res.headers['content-disposition'] ?? '');
            const star = disposition.match(/filename\*=UTF-8''([^;]+)/i);
            const plain = disposition.match(/filename="([^"]+)"/i);
            const filename = star
                ? decodeURIComponent(star[1])
                : plain?.[1] || `${campaign.name}.xlsx`;
            const url = URL.createObjectURL(res.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.click();
            URL.revokeObjectURL(url);
        } catch {
            notification.error({ message: 'Export failed' });
        } finally {
            setExporting(false);
        }
    };

    const confirmRefresh = () => {
        Modal.confirm({
            title: 'Refresh campaign content?',
            content:
                'This will fetch posts and stories from approved creators. Keyword matches are published; everything else is saved as drafts. ' +
                'While the campaign is active and today is between its start and end dates, that fetch also runs automatically once per day. ' +
                'Manual refresh calls external APIs and can take a while — use it when you need data sooner.',
            okText: 'Refresh',
            cancelText: 'Cancel',
            onOk: runRefresh,
        });
    };

    const onRefresh = () => {
        if (!campaignId || refreshing || refresh.isLoading) return;
        if (!campaign?.start_date || !campaign?.end_date) {
            setDateStart(campaign?.start_date ? dayjs(campaign.start_date) : null);
            setDateEnd(campaign?.end_date ? dayjs(campaign.end_date) : null);
            setDatesOpen(true);
            return;
        }
        confirmRefresh();
    };

    const saveDatesAndRefresh = async () => {
        if (!campaignId || !dateStart || !dateEnd) return;
        setSavingDates(true);
        try {
            const start_date = dateStart.startOf('day').toISOString();
            const end_date = dateEnd.endOf('day').toISOString();
            await axiosApiInstance.patch(`/api/campaigns/items/${campaignId}/dates`, { start_date, end_date });
            qc.setQueryData<Campaign>(['campaigns', 'detail', campaignId], (prev) =>
                prev ? { ...prev, start_date, end_date } : prev,
            );
            setDatesOpen(false);
            confirmRefresh();
        } catch (e: any) {
            notification.error({ message: e?.response?.data?.error || 'Could not save dates' });
        } finally {
            setSavingDates(false);
        }
    };

    if (isLoading) {
        return <div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>;
    }
    if (isError || !campaign) {
        return (
            <div style={{ padding: 48 }}>
                <Empty description="Campaign not found">
                    <Button onClick={() => navigate('/creators/campaigns')}>All campaigns</Button>
                </Empty>
            </div>
        );
    }

    const base = `/creators/campaigns/${campaign.id}`;
    const title = TITLES[section] || campaign.name;
    const refreshing = campaign.refresh_status === 'running';

    return (
        <Layout
            style={{
                // ProLayout pads the page 40px / 24px. Pull the rail back so it
                // sits flush with the header and the app sidebar.
                margin: '-24px -40px',
                minHeight: 'calc(100vh - 56px)',
                background: '#fff',
            }}
        >
            <Sider
                width={248}
                collapsedWidth={72}
                collapsible
                collapsed={siderCollapsed}
                onCollapse={onSiderCollapse}
                theme="light"
                style={{
                    borderRight: '1px solid #f0f0f0',
                    background: '#fafafa',
                }}
            >
                <div style={{ padding: siderCollapsed ? '16px 8px 8px' : '20px 16px 12px 20px' }}>
                    <Link to="/creators/campaigns" style={{ fontSize: 14, color: '#8c8c8c' }}>
                        {siderCollapsed ? '←' : '← All campaigns'}
                    </Link>
                    {!siderCollapsed && (
                        <Typography.Title level={4} style={{ margin: '10px 0 0', fontSize: 18, fontWeight: 600 }} ellipsis>
                            {campaign.name}
                        </Typography.Title>
                    )}
                </div>
                <Menu
                    className="campaign-section-nav"
                    mode="inline"
                    inlineCollapsed={siderCollapsed}
                    selectedKeys={[section]}
                    style={{ border: 'none', background: 'transparent' }}
                    items={SECTIONS.map((s) => ({
                        key: s.key,
                        icon: s.icon,
                        label: s.label,
                    }))}
                    onClick={({ key }) => navigate(`${base}/${key}`)}
                />
                <style>{`
                    .campaign-section-nav.ant-menu-inline {
                        border-inline-end: none !important;
                    }
                    .campaign-section-nav.ant-menu-inline > .ant-menu-item {
                        display: flex;
                        align-items: center;
                        margin-inline: 8px;
                        width: calc(100% - 16px);
                        padding-inline: 12px;
                        height: 44px;
                        line-height: 44px;
                        font-size: 16px;
                    }
                    .campaign-section-nav.ant-menu-inline > .ant-menu-item .ant-menu-item-icon {
                        font-size: 18px;
                        line-height: 0;
                    }
                    .campaign-section-nav.ant-menu-inline > .ant-menu-item .ant-menu-title-content {
                        margin-inline-start: 12px;
                        line-height: 44px;
                        font-size: 16px;
                    }
                    .campaign-section-nav.ant-menu-inline-collapsed > .ant-menu-item {
                        padding-inline: calc(50% - 18px) !important;
                    }
                `}</style>
            </Sider>
            <Content style={{ padding: '16px 24px 32px', minWidth: 0 }}>
                <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 8 }}>
                    <Link to="/creators/campaigns">All campaigns</Link>
                    {' › '}
                    <Link to={`${base}/creators`}>{campaign.name}</Link>
                    {section !== 'creators' && ` › ${title}`}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 16 }}>
                    <div>
                        <Typography.Title level={3} style={{ margin: 0 }}>{title}</Typography.Title>
                        <Space size={8} style={{ marginTop: 4, color: '#8c8c8c', fontSize: 13 }} wrap>
                            {campaign.brand?.name && <Tag color="geekblue" style={{ margin: 0 }}>{campaign.brand.name}</Tag>}
                            {(campaign.start_date || campaign.end_date) && (
                                <span>{formatDateRange(campaign.start_date, campaign.end_date)}</span>
                            )}
                        </Space>
                    </div>
                    <Space>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            {refreshing ? 'Refreshing…' : formatRefreshed(campaign.last_refreshed_at)}
                        </Typography.Text>
                        <Button
                            icon={<DownloadOutlined />}
                            loading={exporting}
                            onClick={onExport}
                        >
                            Export
                        </Button>
                        <Button
                            icon={<ReloadOutlined spin={refreshing} />}
                            onClick={onRefresh}
                            loading={refresh.isLoading || refreshing}
                        >
                            Refresh
                        </Button>
                        <Dropdown
                            menu={{
                                items: [
                                    { key: 'edit', icon: <EditOutlined />, label: 'Edit campaign' },
                                ],
                                onClick: ({ key }) => {
                                    if (key === 'edit') navigate(`${base}/edit`);
                                },
                            }}
                        >
                            <Button>···</Button>
                        </Dropdown>
                    </Space>
                </div>
                {campaign.refresh_error && campaign.refresh_status === 'error' && (
                    <Typography.Text type="danger" style={{ display: 'block', marginBottom: 12 }}>
                        Last refresh failed: {campaign.refresh_error}
                    </Typography.Text>
                )}
                {campaign.refresh_error && campaign.refresh_status !== 'running' && campaign.refresh_status !== 'error' && (
                    <Typography.Text type="warning" style={{ display: 'block', marginBottom: 12 }}>
                        Refresh finished. Some accounts could not be fetched: {campaign.refresh_error}
                    </Typography.Text>
                )}
                <Outlet />
            </Content>
            <Modal
                title="Add campaign dates"
                open={datesOpen}
                okText="Save and refresh"
                confirmLoading={savingDates}
                okButtonProps={{ disabled: !dateStart || !dateEnd || dateEnd.isBefore(dateStart, 'day') }}
                onCancel={() => setDatesOpen(false)}
                onOk={saveDatesAndRefresh}
            >
                <p style={{ marginTop: 0 }}>
                    Refresh only searches posts inside the campaign period. Add a start and an end date before continuing.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                        <div style={{ marginBottom: 6 }}>Start date</div>
                        <DatePicker value={dateStart} onChange={setDateStart} style={{ width: '100%' }} placeholder="Select start date" />
                    </div>
                    <div>
                        <div style={{ marginBottom: 6 }}>End date</div>
                        <DatePicker value={dateEnd} onChange={setDateEnd} style={{ width: '100%' }} placeholder="Select end date" />
                    </div>
                </div>
            </Modal>
        </Layout>
    );
};

export default CampaignLayout;
